/**
 * "What it refuses to do" — the two write guards, rendered as the refusal
 * cards the chat actually renders.
 *
 * TWO PATHS, AND THE SECTION HAS TO KEEP THEM APART. The same section sells a
 * free stdio server you point your own client at AND the hosted workspace, and
 * the two do not enforce the same amount. Where a claim only holds on one of
 * them, the copy says so and the UI carries a scope label:
 *
 *   - Update-set guard — FREE AND HOSTED, no configuration.
 *     `shared/update-set-guard.ts#requiresUpdateSet` keys off the tool's
 *     category/subcategory only; there is no environment condition, so it
 *     fires on the first config write of any session on any instance. It is
 *     lifted for the REST OF THAT SESSION by an ensure/create/switch (or by an
 *     explicit `__skipUpdateSet`, which `recordUpdateSetSkip` makes durable) —
 *     see the file's own header. `guards.closer` therefore does not claim
 *     "per call" for this one.
 *
 *   - Prod-write guard — ONLY ON A CLASSIFIED INSTANCE.
 *     `handlers/call-tool.ts:173` gates on `context.environmentType ===
 *     "production"`, and on the stdio path that value has exactly one source:
 *     `SERVICENOW_ENVIRONMENT_TYPE` / `SNOW_ENVIRONMENT_TYPE`
 *     (`shared/context-loader.ts:405-408` — "Lets a self-hosted CLI user mark
 *     an instance 'production' so the call-tool prod-write guard applies.
 *     Invalid/absent ⇒ undefined."). No URL heuristic, and `loadFromAuthJson`
 *     never sets it. So the guard does not fire for a developer who just runs
 *     the install command — which is why `PROD_ENV_VAR` below is printed under
 *     the card, the same treatment `deferred.optOut` gives SNOW_LAZY_TOOLS.
 *     Hosted gets the classification from the instance record instead
 *     (`services/instance-discovery.ts#inferEnvironmentType`, user-confirmed).
 *
 *   - The legend panel — HOSTED ONLY, and labelled as such.
 *     `stripGuardFlags`, `hashToolArgs` and `APPROVAL_TTL_MS` all live in
 *     `portal/backend/src/services/guard-approval.ts`. There is no equivalent
 *     in the OSS package: call-tool.ts:174 and tools/meta/index.ts:513 read
 *     `args.__confirmProd` straight off model output, and
 *     `prodWriteBlockMessage()` names the flag to the model. On the free path
 *     the model is obliged to surface the refusal, not prevented from
 *     answering it — `guards.stripped.body` says that in those words.
 *
 * Everything else on screen is copied from source, not invented:
 *
 *  - The two refusal sentences come from `guards.updateSet.refusal` and
 *    `guards.prod.refusal`, verbatim from the live cards in
 *    `components/chat/ToolInvocationCard.tsx:612` and `:615`.
 *
 *  - `snow_create_business_rule` (category `development`) is gated by the
 *    update-set guard; `snow_record_manage` (category `core-operations`)
 *    is EXEMPT from it and gated only by the prod-write guard. That pairing
 *    is the section's honest edge, and it is what
 *    `shared/update-set-guard.ts` (`EXEMPT_CATEGORIES`) actually does — see
 *    `guards.dataWritesNote`.
 *
 *  - The environment chips are the literal values of `environmentType`
 *    ("production" | "development" | "test" | "uat" | "sandbox") from the
 *    OSS auth context.
 *
 * The TTL appears ONCE on this page, in the legend, in the constant's own
 * source form — deliberately not also spelled out in prose, so there is only
 * one copy of it to go stale against guard-approval.ts.
 *
 * Every user-visible sentence comes from the `guards` namespace. The only
 * literals in this file are code identifiers (tool names, argument keys, flag
 * names, enum values, an env var, a constant declaration) — they read the same
 * in both locales, which is why they are not translated.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Tag } from '../serac';
import { useIsMobile } from '../../hooks/useMediaQuery';

/**
 * The one thing that turns the prod-write guard on when you run the server
 * yourself. `ENV_VARS.environmentType` in the OSS context-loader; the guard is
 * inert without it.
 */
const PROD_ENV_VAR = 'SERVICENOW_ENVIRONMENT_TYPE=production';

/** A blocked call, as the tool card shows it before a human touches it. */
interface RefusalCardProps {
  /** Section copy — the claim this guard backs. */
  title: string;
  body: string;
  /** Verbatim refusal text, straight out of the chat's guard card. */
  refusal: string;
  /** Optional limit of the guard, printed as a source comment. */
  note?: string;
  /** Optional precondition, printed as bare source under the card. */
  envVar?: string;
  /** Real tool from the OSS catalogue. */
  toolName: string;
  /** What the card's own `summariseArgs` would pull out of these arguments. */
  argSummary: string;
  /** Arguments as the server would run them, guard flags already gone. */
  args: string[];
  /** `environmentType` of the instance the call was aimed at. */
  environment: string;
  /** Warn-tinted when the environment itself is what blocked the call. */
  environmentTone: 'warn' | 'neutral';
}

const monoStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontFeatureSettings: '"zero", "ss01"',
};

function RefusalCard({
  title,
  body,
  refusal,
  note,
  envVar,
  toolName,
  argSummary,
  args,
  environment,
  environmentTone,
}: RefusalCardProps) {
  const isMobile = useIsMobile();
  const warnEdge = 'color-mix(in oklch, var(--signal-warn) 30%, var(--border))';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
      <div>
        <h3
          style={{
            margin: 0,
            fontSize: isMobile ? 17 : 19,
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
            color: 'var(--fg)',
            fontWeight: 500,
          }}
        >
          {title}
        </h3>
        <p
          style={{
            margin: '10px 0 0',
            fontSize: isMobile ? 14 : 15,
            lineHeight: 1.6,
            color: 'var(--fg-muted)',
          }}
        >
          {body}
        </p>
      </div>

      {/* The tool card, in the state the guard leaves it in. */}
      <div
        style={{
          borderRadius: 10,
          border: `1px solid ${warnEdge}`,
          background: 'color-mix(in oklch, var(--signal-warn) 6%, var(--surface))',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '7px 10px',
            minWidth: 0,
          }}
        >
          <span style={{ display: 'flex', color: 'var(--signal-warn)', flexShrink: 0 }}>
            <Icon.shield s={14} />
          </span>
          {/* Shrinks only after argSummary has collapsed — at the two-column
              grid's narrowest (~768px) the interior is barely wider than
              name + chip, and a rigid name pushed the chip out of the
              overflow-hidden card instead of clipping the summary. */}
          <span
            style={{
              ...monoStyle,
              fontSize: 12,
              color: 'var(--fg)',
              flexShrink: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {toolName}
          </span>
          <span
            style={{
              ...monoStyle,
              fontSize: 11,
              color: 'var(--fg-dim)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexShrink: 500,
              minWidth: 0,
            }}
          >
            {argSummary}
          </span>
          <span style={{ marginLeft: 'auto', flexShrink: 0 }}>
            <Tag tone={environmentTone}>{environment}</Tag>
          </span>
        </div>

        <pre
          style={{
            ...monoStyle,
            margin: 0,
            padding: '0 10px 8px',
            fontSize: 11,
            lineHeight: 1.7,
            color: 'var(--fg-muted)',
            whiteSpace: 'pre',
            overflowX: 'auto',
          }}
        >
          {args.join('\n')}
        </pre>

        <div
          style={{
            padding: '9px 10px 11px',
            borderTop: `1px solid color-mix(in oklch, var(--signal-warn) 25%, var(--border))`,
          }}
        >
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--fg)' }}>
            {refusal}
          </p>
        </div>
      </div>

      {envVar && (
        <code
          style={{
            ...monoStyle,
            display: 'block',
            fontSize: 12,
            lineHeight: 1.6,
            color: 'var(--fg-muted)',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {envVar}
        </code>
      )}

      {note && (
        <p
          style={{
            ...monoStyle,
            margin: 0,
            fontSize: 11.5,
            lineHeight: 1.65,
            color: 'var(--fg-dim)',
          }}
        >
          <span aria-hidden="true">{'// '}</span>
          {note}
        </p>
      )}
    </div>
  );
}

/** One line of the legend: a source symbol and what happens to it. */
function LegendRow({
  glyph,
  tone,
  code,
  strike,
}: {
  glyph: 'x' | 'check' | 'clock';
  tone: string;
  code: string;
  strike?: boolean;
}) {
  const Glyph = Icon[glyph];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <span style={{ display: 'flex', color: tone, flexShrink: 0 }}>
        <Glyph s={13} />
      </span>
      <span
        style={{
          ...monoStyle,
          fontSize: 12,
          color: strike ? 'var(--fg-dim)' : 'var(--fg)',
          textDecoration: strike ? 'line-through' : 'none',
          textDecorationThickness: strike ? '1px' : undefined,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {code}
      </span>
    </div>
  );
}

export default function SectionGuards() {
  const { t } = useTranslation('marketing');
  const isMobile = useIsMobile();

  return (
    <section
      style={{
        padding: isMobile ? '56px 16px' : '80px 32px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div className="t-eyebrow" style={{ marginBottom: 16 }}>
          {t('guards.eyebrow')}
        </div>
        <h2
          className="t-display"
          style={{
            fontSize: 'clamp(28px, 5vw, 44px)',
            margin: '0 0 14px',
            maxWidth: 780,
            lineHeight: 1.15,
          }}
        >
          {t('guards.headline')}
        </h2>
        <p
          style={{
            fontSize: isMobile ? 15 : 17,
            lineHeight: 1.6,
            color: 'var(--fg-muted)',
            maxWidth: 720,
            margin: '0 0 20px',
          }}
        >
          {t('guards.body')}
        </p>

        {/* The two cards below are the hosted chat's UI. Say so before the
            reader assumes the free server draws them. */}
        <p
          style={{
            ...monoStyle,
            margin: `0 0 ${isMobile ? 32 : 44}px`,
            maxWidth: 720,
            fontSize: 11.5,
            lineHeight: 1.65,
            color: 'var(--fg-dim)',
          }}
        >
          <span aria-hidden="true">{'// '}</span>
          {t('guards.cardsNote')}
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: isMobile ? 32 : 40,
            alignItems: 'start',
          }}
        >
          {/* A configuration write with no active update set. No environment
              condition — hence the `development` chip, and hence this is the
              half of the section that holds on the free server untouched. */}
          <RefusalCard
            title={t('guards.updateSet.title')}
            body={t('guards.updateSet.body')}
            refusal={t('guards.updateSet.refusal')}
            note={t('guards.dataWritesNote')}
            toolName="snow_create_business_rule"
            argSummary="auto_assign_p1"
            args={[
              '{',
              '  "name": "auto_assign_p1",',
              '  "table": "incident",',
              '  "when": "before"',
              '}',
            ]}
            environment="development"
            environmentTone="neutral"
          />

          {/* Any write against a production-CLASSIFIED instance — including the
              data writes the update-set guard deliberately lets past. Inert
              until the instance carries that classification, so the env var
              that supplies it on stdio is printed with the card. */}
          <RefusalCard
            title={t('guards.prod.title')}
            body={t('guards.prod.body')}
            refusal={t('guards.prod.refusal')}
            envVar={PROD_ENV_VAR}
            toolName="snow_record_manage"
            argSummary="incident"
            args={[
              '{',
              '  "action": "update",',
              '  "table": "incident",',
              '  "sys_id": "d71f7935c0a8016700802b64c67c11c6"',
              '}',
            ]}
            environment="production"
            environmentTone="warn"
          />
        </div>

        {/* Who is allowed to lift the refusal — hosted only, and it says so. */}
        <div
          style={{
            marginTop: isMobile ? 36 : 48,
            padding: isMobile ? 18 : 24,
            background: 'var(--ink-050)',
            border: '1px solid var(--border)',
            borderRadius: 14,
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.15fr) minmax(0, 1fr)',
            gap: isMobile ? 20 : 40,
            alignItems: 'center',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h3
              style={{
                margin: 0,
                fontSize: isMobile ? 17 : 19,
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
                color: 'var(--fg)',
                fontWeight: 500,
              }}
            >
              {t('guards.stripped.title')}
            </h3>
            <p
              style={{
                margin: '10px 0 0',
                fontSize: isMobile ? 14 : 15,
                lineHeight: 1.6,
                color: 'var(--fg-muted)',
              }}
            >
              {t('guards.stripped.body')}
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              padding: isMobile ? 14 : 16,
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              minWidth: 0,
            }}
          >
            {/* Scope label on the panel itself: a skimmer reads the glyphs and
                the struck-through flags, and every mechanism below this line
                is portal-side (services/guard-approval.ts), not OSS. */}
            <div style={{ display: 'flex' }}>
              <Tag tone="neutral">{t('guards.stripped.scopeLabel')}</Tag>
            </div>
            <div style={{ height: 1, background: 'var(--border)', margin: '2px 0' }} />
            {/* Removed from anything the model writes, at any depth. */}
            <LegendRow glyph="x" tone="var(--signal-fail)" code="__confirmProd" strike />
            <LegendRow glyph="x" tone="var(--signal-fail)" code="__skipUpdateSet" strike />
            <div style={{ height: 1, background: 'var(--border)', margin: '2px 0' }} />
            {/* What a human approval is bound to, and how long it lives. */}
            <LegendRow glyph="check" tone="var(--signal-ok)" code="sha256(stableStringify(args))" />
            <LegendRow glyph="clock" tone="var(--fg-dim)" code="APPROVAL_TTL_MS = 30 * 60 * 1000" />
          </div>
        </div>

        <div
          style={{
            marginTop: isMobile ? 28 : 40,
            paddingTop: isMobile ? 20 : 24,
            borderTop: '1px solid var(--border)',
          }}
        >
          <p
            className="t-display"
            style={{
              margin: 0,
              fontSize: isMobile ? 18 : 22,
              lineHeight: 1.45,
              color: 'var(--fg)',
              maxWidth: 860,
            }}
          >
            {t('guards.closer')}
          </p>
        </div>
      </div>
    </section>
  );
}
